import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
    Box,
    Card,
    CircularProgress,
    Typography,
    useTheme,
} from '@mui/material'
import { ReceiptLong } from '@mui/icons-material'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { useTenantInvoices } from '../../hooks/useTenantInvoices'
import { RootState } from '../../store'
import { InvoiceCard } from './InvoiceCard'

export const Invoices = () => {
    const theme = useTheme()
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const leaseId = selectedLease?.id

    const { invoices, isLoading, getTenantInvoices } = useTenantInvoices(leaseId)

    useEffect(() => {
        getTenantInvoices()
    }, [leaseId, getTenantInvoices])

    if (!leaseId) {
        return (
            <ResponsivePageContainer
                title="Invoices"
                subtitle="View invoices for work performed at your unit."
            >
                <Typography color="textSecondary">
                    Select a lease to view invoices.
                </Typography>
            </ResponsivePageContainer>
        )
    }

    const results = invoices?.results ?? []

    return (
        <ResponsivePageContainer
            title="Invoices"
            subtitle="View invoices for work performed at your unit."
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: '800px',
                    width: '100%',
                }}
            >
                {isLoading ? (
                    <FlexDiv
                        style={{
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                            width: '100%',
                        }}
                    >
                        <CircularProgress
                            size={40}
                            sx={{ color: theme.palette.primary.main }}
                        />
                    </FlexDiv>
                ) : results.length === 0 ? (
                    <Card
                        sx={{
                            bgcolor: 'rgba(138, 142, 140, 0.16)',
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid rgba(255,255,255,0.1)',
                            p: 3,
                            textAlign: 'center',
                        }}
                    >
                        <ReceiptLong
                            sx={{
                                fontSize: 48,
                                color: 'rgba(255,255,255,0.5)',
                                mb: 2,
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}
                        >
                            No Invoices
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                            No invoices to display for this lease.
                        </Typography>
                    </Card>
                ) : (
                    results.map((invoice) => (
                        <InvoiceCard key={invoice.id} invoice={invoice} />
                    ))
                )}
            </Box>
        </ResponsivePageContainer>
    )
}
